import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hero carousel — full-bleed feature card that rotates through the top
 * candidates from the discover feed. Drawn from the highest-priority rail
 * with a real cover image (Continue Playing → Top Plays → New Arrivals).
 *
 * Auto-rotates every 6s; pause-on-hover so users can read the title; tap
 * anywhere on the hero opens the player/reader for that item.
 */
const resolveCover = (src) => {
  if (!src) return null;
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  if (src.startsWith('/api/')) return `${BACKEND_URL}${src}`;
  if (src.startsWith('/uploads/')) return `${BACKEND_URL}/api${src}`;
  return src;
};

const fmtCount = (n) => {
  if (!n) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

/**
 * Pick the best 5 hero candidates from the rails:
 * 1. Continue Playing items (resume with one tap)
 * 2. Most Played series with covers
 * 3. New Arrivals with covers
 * Filter out anything without a cover image — the hero is image-first.
 */
const buildHeroSet = (rails) => {
  const seen = new Set();
  const picks = [];
  const sources = [
    rails.find((r) => r.id === 'continue'),
    rails.find((r) => r.id === 'top_plays'),
    rails.find((r) => r.id === 'top_completed'),
    rails.find((r) => r.id === 'new_arrivals'),
  ].filter(Boolean);

  for (const rail of sources) {
    for (const it of rail.items || []) {
      if (!it?.cover_image) continue;
      const id = it.book_id || it.series_id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      picks.push({ ...it, _rail: rail.id });
      if (picks.length >= 5) return picks;
    }
  }
  return picks;
};

const HeroCarousel = ({ rails }) => {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const heroes = buildHeroSet(rails);

  useEffect(() => {
    if (heroes.length <= 1 || paused) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % heroes.length);
    }, 6000);
    return () => clearInterval(t);
  }, [heroes.length, paused]);

  if (heroes.length === 0) return null;

  const current = heroes[idx];
  const cover = resolveCover(current.cover_image);
  const onPlay = () => {
    if (current._rail === 'continue') {
      const id = current.book_id;
      const type = current.type || 'audiobook';
      navigate(type === 'ebook' ? `/reader/${id}` : `/player/${id}`);
      return;
    }
    const id = current.book_id || current.series_id;
    if (current.is_series) {
      navigate(`/library?focus=${encodeURIComponent(id)}`);
    } else if ((current.type || 'ebook') === 'ebook') {
      navigate(`/reader/${id}`);
    } else {
      navigate(`/player/${id}`);
    }
  };

  const railLabel = {
    continue: 'Continue Playing',
    top_plays: 'Most Played',
    top_completed: 'Top Completed',
    new_arrivals: 'New Arrival',
  }[current._rail] || 'Featured';

  return (
    <div
      className="relative mx-3 sm:mx-6 mb-6 sm:mb-8 rounded-2xl overflow-hidden ring-1 ring-slate-700/50 group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="discover-hero"
    >
      <div className="relative aspect-[16/9] sm:aspect-[21/8]">
        {cover && (
          <>
            {/* Crossfade between heroes — render previous-frame as a fading layer
                under the new one. The CSS transition handles the rest. */}
            <img
              key={current.book_id || current.series_id}
              src={cover}
              alt={current.title || ''}
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-md opacity-70"
              loading="eager"
            />
            <img
              src={cover}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[10s] ease-out group-hover:scale-105"
              loading="eager"
            />
          </>
        )}
        {/* Gradient floor for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-950/30 to-transparent" />

        <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs uppercase tracking-[0.2em] font-bold text-amber-300/90">
              <Sparkles className="w-3 h-3" />
              {railLabel}
            </span>
            {current.episode_count > 0 && (
              <span className="text-[10px] sm:text-xs text-slate-300/70">
                · {current.episode_count} episodes
              </span>
            )}
          </div>

          <h2
            className="text-white font-bold tracking-tight drop-shadow-lg line-clamp-2"
            style={{
              fontSize: 'clamp(1.5rem, 5vw, 3rem)',
              lineHeight: 1.05,
              maxWidth: '80%',
            }}
          >
            {current.title}
          </h2>

          {current.author && (
            <p className="mt-1 text-sm sm:text-base text-slate-300 truncate max-w-[70%]">
              by {current.author}
            </p>
          )}

          <div className="mt-4 sm:mt-5 flex items-center gap-3">
            <button
              onClick={onPlay}
              className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold rounded-full px-4 sm:px-6 py-2 sm:py-2.5 text-sm shadow-xl hover:bg-amber-50 active:scale-[0.98] transition-all"
              data-testid="hero-play-btn"
            >
              <Play className="w-4 h-4 fill-current" />
              {current._rail === 'continue' ? 'Resume' : 'Play now'}
            </button>
            {current.play_count > 0 && (
              <span className="text-xs sm:text-sm text-slate-300/90 font-medium">
                {fmtCount(current.play_count)} plays
                {current.rating ? ` · ★ ${Number(current.rating).toFixed(1)}` : ''}
              </span>
            )}
          </div>
        </div>

        {/* Dot navigation */}
        {heroes.length > 1 && (
          <div className="absolute bottom-2 right-3 sm:bottom-4 sm:right-6 flex gap-1.5">
            {heroes.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx
                    ? 'w-6 bg-white'
                    : 'w-1.5 bg-white/40 hover:bg-white/70'
                }`}
                aria-label={`Slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroCarousel;
